import {
  VIOLATION_AGENCIES,
  VIOLATION_AGENCY_LABELS,
  VIOLATION_OPEN_STATUSES,
  type ViolationAgency,
} from '@sitewise/shared';
import {
  Button,
  EmptyState,
  ErrorState,
  SeverityBadge,
  Skeleton,
  cn,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { listViolations, type Violation } from '../../../api/violations.js';

import {
  formatDate,
  formatRelativeDays,
  severityLabel,
  statusLabel,
} from './lib.js';

function isAgency(value: string | undefined): value is ViolationAgency {
  return !!value && (VIOLATION_AGENCIES as readonly string[]).includes(value);
}

/**
 * `/violations/agency/:agency/buildings/:buildingId` — open violations
 * for one agency × building as selectable cards. Select all / open all
 * opens the chosen (or all) violation details in new tabs.
 */
export function ViolationBuildingPage() {
  const { agency: agencyParam, buildingId } = useParams<{
    agency: string;
    buildingId: string;
  }>();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const agency = isAgency(agencyParam) ? agencyParam : null;

  const listQuery = useQuery({
    queryKey: ['violations', 'list', { agency, buildingId, status: 'open' }],
    queryFn: () =>
      listViolations({
        agency: [agency!],
        buildingId: buildingId!,
        status: [...VIOLATION_OPEN_STATUSES],
        limit: 100,
      }),
    enabled: agency != null && !!buildingId,
  });

  const rows = listQuery.data?.violations ?? [];
  const buildingName = rows[0]?.buildingName ?? 'Building';
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  if (!agency || !buildingId) {
    return <Navigate to="/violations" replace />;
  }

  function toggleSelected(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }

  function openIds(ids: string[]) {
    for (const id of ids) {
      window.open(`/violations/${id}`, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <div className="space-y-4 p-4">
      <div className="space-y-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/violations/agency/${agency}`}>
            <ArrowLeft className="h-4 w-4" />
            {VIOLATION_AGENCY_LABELS[agency]} buildings
          </Link>
        </Button>
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {VIOLATION_AGENCY_LABELS[agency]}
            </div>
            <h1 className="text-xl font-semibold">{buildingName}</h1>
            <p className="text-sm text-fg-muted">
              {listQuery.isSuccess
                ? `${rows.length} open ${rows.length === 1 ? 'violation' : 'violations'}`
                : 'Open violations for this building'}
            </p>
          </div>
          {rows.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                {allSelected ? 'Clear selection' : 'Select all'}
              </Button>
              <Button
                size="sm"
                disabled={selected.size === 0}
                onClick={() => openIds(selectedIds)}
              >
                Open selected ({selected.size})
              </Button>
              <Button variant="outline" size="sm" onClick={() => openIds(rows.map((r) => r.id))}>
                Open all
              </Button>
            </div>
          ) : null}
        </header>
      </div>

      {listQuery.isError ? (
        <ErrorState description="Could not load violations for this building." />
      ) : listQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No open violations"
          description="Nothing open for this agency at this building."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((v) => (
            <ViolationCard
              key={v.id}
              violation={v}
              selected={selected.has(v.id)}
              onToggle={() => toggleSelected(v.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ViolationCard({
  violation: v,
  selected,
  onToggle,
}: {
  violation: Violation;
  selected: boolean;
  onToggle: () => void;
}) {
  const cureOverdue =
    v.cureByAt != null && new Date(v.cureByAt).getTime() < Date.now();

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-lg border bg-surface p-4 transition-colors',
        selected ? 'border-fg-secondary bg-surface-muted' : 'border-border',
      )}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          aria-label={`Select ${v.summary}`}
          checked={selected}
          onChange={onToggle}
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge level={v.severity} subtle>
              {severityLabel(v.severity)}
            </SeverityBadge>
            <span className="text-xs text-fg-muted">{statusLabel(v.status)}</span>
            {v.autoWorkOrderId ? (
              <span className="text-xs text-fg-muted">WO linked</span>
            ) : null}
          </div>
          <Link
            to={`/violations/${v.id}`}
            className="mt-2 block font-medium leading-snug hover:underline"
          >
            {v.summary}
          </Link>
          <div className="mt-1 truncate text-xs text-fg-muted">
            {v.kind}
            {v.unitNumber ? ` · Unit ${v.unitNumber}` : ''}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs">
        <div>
          <div className="text-fg-muted">Issued</div>
          <div className="tabular-nums">{formatDate(v.issuedAt)}</div>
          <div className="text-fg-muted">{formatRelativeDays(v.issuedAt)}</div>
        </div>
        <div>
          <div className="text-fg-muted">Cure-by</div>
          <div className={cn('tabular-nums', cureOverdue && 'text-danger')}>
            {formatDate(v.cureByAt)}
          </div>
          <div className={cn(cureOverdue ? 'text-danger' : 'text-fg-muted')}>
            {formatRelativeDays(v.cureByAt)}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/violations/${v.id}`}>View</Link>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(`/violations/${v.id}`, '_blank', 'noopener,noreferrer')}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open
        </Button>
      </div>
    </div>
  );
}
